import { useState, useEffect } from 'react';

export interface UserLocation {
  lat: number;
  lng: number;
}

export function useUserLocation() {
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    const successHandler = (position: GeolocationPosition) => {
      setUserLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      setLocationError(null);
    };

    const errorHandler = (error: GeolocationPositionError) => {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setLocationError('Location permission denied');
          break;
        case error.POSITION_UNAVAILABLE:
          setLocationError('Location unavailable');
          break;
        case error.TIMEOUT:
          setLocationError('Location request timed out');
          break;
        default:
          setLocationError('Unknown location error');
      }
    };

    // Get initial position. Used by Routes / Schedule / Sets for
    // distance-sorting stores - not check-in critical, but still must
    // not accept a 5-minute-old fix or we end up sorting by where the
    // user *was*, not where they are. Accept up to 30s of cache.
    navigator.geolocation.getCurrentPosition(successHandler, errorHandler, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 30000,
    });

    // Watch for position changes - keep fresh, no cache
    const watchId = navigator.geolocation.watchPosition(successHandler, errorHandler, {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 0,
    });

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  return { userLocation, locationError };
}
